import * as React from 'react';
import styled from 'styled-components';
import { QuerySuggestions } from '@packages/common';
import { useTranslations } from '@packages/i18n';

import { Icon, Input, ButtonWithIcon, AutosuggestList } from '../../components';
import { media, themeGet, is, BreakpointNames } from '../../theme';
import { BreakpointMountContext, shouldRender } from '../breakpointMount/BreakpointMount';

const SearchInputPlacehoderIcon = styled.span`
  top: 50%;
  position: absolute;
  right: calc(${themeGet.space(2)} + 3px);
  transform: translateY(-50%);
`;

const SearchInputWrapper = styled.div`
  position: relative;
  ${media.to.lg`
    width: calc(100% - ${themeGet.space(4)});
  `}
`;

const ButtonWithIconStyled = styled(ButtonWithIcon)`
  display: block;
  padding: 0 ${themeGet.space(2)};
  ${'&: first-of-type'} {
    margin-right: calc(${themeGet.space(1)} / 2);
  }
`;

const SearchInputButtonsWrapper = styled.div<{ isFocused: boolean }>`
  height: 100%;
  right: ${themeGet.space(1)};
  top: calc(${themeGet.space(1)} / 2);
  width: calc(${themeGet.space(7)} + ${themeGet.space(1)});
  position: absolute;
  opacity: 0;
  display: flex;
  justify-content: space-between;
  transition: opacity 200ms linear;
  & > ${ButtonWithIconStyled} {
    visibility: hidden;
  }

  ${is('isFocused')`
    opacity: 1;
    & > ${ButtonWithIconStyled} {
      visibility: visible;
    }
  `};
`;

export interface SearchInputBaseProps {
  isActive?: boolean;
  testIdPrefix?: string;
  query: string;
  onQueryChange: (query: string) => void;
  querySuggestions: QuerySuggestions[];
  onQuerySuggestionSubmit: (querySuggestion: QuerySuggestions, position: number) => void;
  onQuerySubmit: (event: React.FormEvent<HTMLFormElement> | React.KeyboardEvent<HTMLInputElement>) => void;
}

export const SearchInput: React.FC<SearchInputBaseProps> = ({
  isActive,
  query,
  onQueryChange,
  querySuggestions,
  onQuerySuggestionSubmit,
  onQuerySubmit,
  testIdPrefix = 'search',
}) => {
  const [searchInputPlaceholderDesktop, searchInputPlaceholderSM, searchInputPlaceholderXS] = useTranslations([
    'searchInputPlaceholder',
    'searchInputPlaceholderSM',
    'searchInputPlaceholderXS',
  ]) as string[];
  const inputRef = React.createRef<HTMLInputElement>();
  const [suggestionIndex, setSuggestionIndex] = React.useState<number>(-1);
  const { currentBreakpoint } = React.useContext(BreakpointMountContext);
  const [placeholderText, setPlaceholderText] = React.useState(searchInputPlaceholderSM);

  React.useEffect(() => {
    const isXS = shouldRender(currentBreakpoint, { max: BreakpointNames.sm });
    const isMobile = shouldRender(currentBreakpoint, { min: BreakpointNames.sm, max: BreakpointNames.md });
    setPlaceholderText((curText) => {
      if (isMobile && curText !== searchInputPlaceholderSM) {
        return searchInputPlaceholderSM;
      } else if (!isMobile && curText !== searchInputPlaceholderDesktop) {
        return searchInputPlaceholderDesktop;
      } else if (isXS && curText !== searchInputPlaceholderXS) {
        return searchInputPlaceholderXS;
      }

      return curText;
    });
  }, [currentBreakpoint, searchInputPlaceholderDesktop, searchInputPlaceholderSM, searchInputPlaceholderXS]);

  React.useEffect(() => {
    setSuggestionIndex(-1);
  }, [querySuggestions]);

  React.useEffect(() => {
    if (isActive && inputRef.current) {
      inputRef.current.focus();
    }
  }, [inputRef, isActive]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    onQueryChange(value);
  };

  const handleClear = () => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
    onQueryChange('');
  };

  const handleKeydown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!querySuggestions) {
      return;
    }

    const suggestionsLength = querySuggestions.length;

    switch (event.which) {
      case 40:
        event.preventDefault();
        setSuggestionIndex((index) => (index + 1) % suggestionsLength);
        break;
      case 38:
        event.preventDefault();
        setSuggestionIndex((index) => (index - 1 + suggestionsLength) % suggestionsLength);
        break;
      case 13:
        event.preventDefault();
        const suggestion = querySuggestions[suggestionIndex];
        suggestion ? onQuerySuggestionSubmit(suggestion, suggestionIndex) : onQuerySubmit(event);
        break;
    }
  };

  return (
    <form onSubmit={onQuerySubmit} noValidate={true}>
      <SearchInputWrapper>
        <Input
          ref={inputRef}
          onKeyDown={handleKeydown}
          value={query}
          autoFocus={false}
          placeholder={placeholderText}
          onChange={handleChange}
          data-testid={`${testIdPrefix}-input`}
        />
        <SearchInputPlacehoderIcon>
          <Icon name="search" size={16} color="sandDune" />
        </SearchInputPlacehoderIcon>
        <SearchInputButtonsWrapper isFocused={query !== ''}>
          <ButtonWithIconStyled
            onClick={handleClear}
            variant="tertiary"
            iconName="close"
            iconColor="sandDune"
            data-testid={`${testIdPrefix}-clear-button`}
          />
          <ButtonWithIconStyled type="submit" iconName="search" data-testid={`${testIdPrefix}-submit-button`} />
        </SearchInputButtonsWrapper>
        <AutosuggestList
          hits={querySuggestions || []}
          handleSelect={onQuerySuggestionSubmit}
          activeIndex={suggestionIndex}
        />
      </SearchInputWrapper>
    </form>
  );
};
